import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import {
  ThumbnailManifestType as VideoJsThumbnailManifestType,
  VideoPlayerHandle,
} from "@fronterahealth/frontera-ui-components";

import { ThumbnailManifestType } from "@api/graphql/types-and-hooks";
import { VideoPlayerContent } from "@pages/VideoSessionsV2/components/VideoPlayerContent";

interface VideoMomentPlayerProps {
  videoUrl: string;
  startTime: number;
  endTime: number;
  thumbnailManifest?: ThumbnailManifestType | null;
  onPause?: () => void;
  onDurationChange?: (duration: number) => void;
}

export const VideoMomentPlayer = forwardRef<VideoPlayerHandle, VideoMomentPlayerProps>(
  ({ videoUrl, startTime, endTime, thumbnailManifest, onPause, onDurationChange }, ref) => {
    const videoPlayerRef = useRef<VideoPlayerHandle>(null);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      loopInRange: (startTime: number, endTime: number) => {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.loopInRange(startTime, endTime);
        }
      },
      isLooping: !!videoPlayerRef.current?.isLooping,
      stopLooping: () => {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.stopLooping();
        }
      },
      play: () => {
        if (videoPlayerRef.current) {
          handlePlayerReady();
        }
      },
      seekTo: (time: number) => {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.seekTo(time);
        }
      },
      playInRange: (startTime: number, endTime: number) => {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.playInRange(startTime, endTime);
        }
      },
      player: videoPlayerRef.current?.player,
      cleanup: () => {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.cleanup();
        }
      },
    }));

    const handlePlayerReady = useCallback(() => {
      if (startTime && endTime) {
        videoPlayerRef.current?.player?.play();
        videoPlayerRef.current?.playInRange(startTime, endTime);
      }
    }, [startTime, endTime]);

    const handleStopLooping = useCallback(() => {
      if (videoPlayerRef.current?.isLooping) {
        videoPlayerRef.current?.stopLooping();
        onPause?.();
      }
    }, [videoPlayerRef.current?.isLooping]);

    useEffect(() => {
      const skipButtons = document.querySelectorAll(".vjs-skip-forward-10, .vjs-skip-backward-10");
      skipButtons.forEach((btn) => btn.addEventListener("click", handleStopLooping));
      return () => {
        skipButtons.forEach((btn) => btn.removeEventListener("click", handleStopLooping));
      };
    }, []);
    const MemoizedVideoClipPlayer = useMemo(
      () => (
        <VideoPlayerContent
          ref={videoPlayerRef}
          videoUrl={videoUrl}
          thumbnailManifest={thumbnailManifest as VideoJsThumbnailManifestType}
          onPlayerReady={handlePlayerReady}
          enabledFocus={true}
          onPause={() => {
            handleStopLooping();
          }}
          onDurationChange={(duration) => {
            onDurationChange?.(duration);
          }}
        />
      ),
      [videoUrl],
    );

    return (
      <div className="w-full aspect-video" data-video-player>
        {MemoizedVideoClipPlayer}
      </div>
    );
  },
);

VideoMomentPlayer.displayName = "VideoMomentPlayer";
